import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"

// Connects to data-controller="datepicker"
export default class extends Controller {
  static values = {
    format: String,
    locale: String,
    timepicker: Boolean,
    position: String,
    minDate: String,
    maxDate: String,
    startDate: String,
    multipleDates: Boolean,
    range: Boolean,
    view: String,
    dateFormat: String,
    altField: String,
    buttons: Array
  }

  static targets = ["input", "hiddenField"]

  connect() {
    console.log("Datepicker controller connected")
    console.log("Has input target:", this.hasInputTarget)
    console.log("Has hidden field target:", this.hasHiddenFieldTarget)
    this.initializeDatepicker()
  }

  disconnect() {
    if (this.datepicker) {
      this.datepicker.destroy()
    }
  }

  initializeDatepicker() {
    const options = this.buildOptions()
    console.log("Datepicker options:", options)

    // Устанавливаем начальную дату из скрытого поля
    if (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value) {
      const initialDate = this.parseRailsDate(this.hiddenFieldTarget.value)
      console.log("Initial date from hidden field:", this.hiddenFieldTarget.value, "parsed:", initialDate)
      if (initialDate && !isNaN(initialDate)) {
        options.selectedDates = [initialDate]
        
        // Также установим отформатированное значение в видимый input
        if (this.hasInputTarget) {
          const displayValue = this.formatDateForDisplay(initialDate)
          console.log("Setting initial display value:", displayValue)
          this.inputTarget.value = displayValue
        }
      }
    }

    // Добавляем callback для отслеживания выбора даты
    if (this.hasHiddenFieldTarget) {
      console.log("Adding onSelect callback to options...")
      options.onSelect = ({ date, formattedDate, datepicker }) => {
        console.log("Air Datepicker onSelect event triggered:", { date, formattedDate })
        this.updateHiddenField(date)
      }
    }

    this.datepicker = new AirDatepicker(this.inputTarget, options)
    console.log("Air Datepicker instance created:", this.datepicker)
  }

  buildOptions() {
    const options = {
      locale: this.getLocale(),
      position: this.positionValue || 'bottom left',
      autoClose: true,
      toggleSelected: false
    }

    // Настройки формата даты
    if (this.hasDateFormatValue) {
      options.dateFormat = this.dateFormatValue
    } else if (this.timepickerValue) {
      options.dateFormat = 'dd.MM.yyyy HH:mm'
    } else {
      options.dateFormat = 'dd.MM.yyyy'
    }

    // Timepicker
    if (this.timepickerValue) {
      options.timepicker = true
      options.timeFormat = 'HH:mm'
    }

    // Диапазон дат
    if (this.minDateValue) {
      options.minDate = new Date(this.minDateValue)
    }

    if (this.maxDateValue) {
      options.maxDate = new Date(this.maxDateValue)
    }

    // Стартовая дата
    if (this.startDateValue) {
      options.startDate = new Date(this.startDateValue)
    }

    // Множественный выбор
    if (this.multipleDatesValue) {
      options.multipleDates = true
      options.multipleDatesSeparator = ', '
    }

    // Диапазон
    if (this.rangeValue) {
      options.range = true
    }

    // Вид по умолчанию
    if (this.viewValue) {
      options.view = this.viewValue
    }

    // Кнопки
    if (this.hasButtonsValue && this.buttonsValue.length > 0) {
      options.buttons = this.buttonsValue
    } else {
      options.buttons = [
        {
          content: 'Сегодня',
          className: 'btn btn-sm btn-outline-primary',
          onClick: (dp) => {
            dp.selectDate(new Date())
            dp.hide()
          }
        },
        {
          content: 'Очистить',
          className: 'btn btn-sm btn-outline-secondary',
          onClick: (dp) => {
            console.log("Clear button clicked")
            dp.clear()
            // Очищаем и скрытое поле
            if (this.hasHiddenFieldTarget) {
              console.log("Clearing hidden field")
              this.hiddenFieldTarget.value = ''
              this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
            }
            dp.hide()
          }
        }
      ]
    }

    // Навигация
    options.navTitles = {
      days: 'MMMM yyyy',
      months: 'yyyy',
      years: 'yyyy1 - yyyy2'
    }

    // Кастомизация классов
    options.classes = 'air-datepicker-custom'

    return options
  }

  getLocale() {
    // Если передан объект локали, используем его
    if (this.localeValue && typeof this.localeValue === 'object') {
      return this.localeValue
    }

    // Определяем локаль из HTML lang атрибута, значения data-атрибута или по умолчанию русская
    const localeString = this.localeValue || document.documentElement.lang || 'ru'
    
    if (localeString === 'ru' || localeString === 'ru-RU') {
      return {
        days: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
        daysShort: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        daysMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
        months: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
        monthsShort: ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
        today: 'Сегодня',
        clear: 'Очистить',
        dateFormat: 'dd.MM.yyyy',
        timeFormat: 'HH:mm',
        firstDay: 1
      }
    }

    // Возвращаем объект английской локали для всех остальных случаев
    return {
      days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      daysShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      daysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      today: 'Today',
      clear: 'Clear',
      dateFormat: 'dd/MM/yyyy',
      timeFormat: 'HH:mm',
      firstDay: 0
    }
  }

  updateHiddenField(date) {
    if (!this.hasHiddenFieldTarget || !date) {
      console.log("updateHiddenField: no hidden field or no date", { hasHiddenField: this.hasHiddenFieldTarget, date })
      return
    }

    let value
    if (Array.isArray(date)) {
      // Для множественного выбора или диапазона
      value = date.map(d => this.formatDateForRails(d)).join(',')
    } else {
      value = this.formatDateForRails(date)
    }

    console.log("Updating hidden field:", { oldValue: this.hiddenFieldTarget.value, newValue: value })
    this.hiddenFieldTarget.value = value
    this.hiddenFieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  parseRailsDate(dateString) {
    if (!dateString) return null

    // Парсим дату из Rails формата в локальном времени
    if (dateString.includes('T')) {
      // Для datetime формата YYYY-MM-DDTHH:MM
      // Добавляем :00 для секунд если их нет, чтобы избежать проблем с парсингом
      const normalizedString = dateString.length === 16 ? dateString + ':00' : dateString
      // Используем конструктор Date с компонентами для избежания проблем с временными зонами
      const match = normalizedString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
      if (match) {
        const [, year, month, day, hours, minutes] = match
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes))
      }
    } else {
      // Для date формата YYYY-MM-DD
      const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (match) {
        const [, year, month, day] = match
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      }
    }

    // Fallback к стандартному парсингу
    return new Date(dateString)
  }

  formatDateForRails(date) {
    if (!date) return ''

    // Форматируем дату для Rails в локальном времени (не UTC)
    if (this.timepickerValue) {
      // Для datetime - используем локальное время в формате YYYY-MM-DDTHH:MM
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    } else {
      // Для date - только дата в локальном времени
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }

  formatDateForDisplay(date) {
    if (!date) return ''

    const locale = this.getLocale()
    
    if (Array.isArray(date)) {
      return date.map(d => this.formatSingleDateForDisplay(d, locale)).join(', ')
    } else {
      return this.formatSingleDateForDisplay(date, locale)
    }
  }

  formatSingleDateForDisplay(date, locale) {
    if (this.timepickerValue) {
      // Для datetime используем локализованное форматирование
      const options = { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }
      return new Intl.DateTimeFormat('ru-RU', options).format(date).replace(/(\d{2})\.(\d{2})\.(\d{4}),\s*(\d{2}):(\d{2})/, '$1.$2.$3 $4:$5')
    } else {
      // Для date используем простой формат dd.MM.yyyy
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}.${month}.${year}`
    }
  }

  // Публичные методы для внешнего управления
  selectDate(date) {
    if (this.datepicker) {
      this.datepicker.selectDate(date)
    }
  }

  clear() {
    if (this.datepicker) {
      this.datepicker.clear()
    }
  }

  show() {
    if (this.datepicker) {
      this.datepicker.show()
    }
  }

  hide() {
    if (this.datepicker) {
      this.datepicker.hide()
    }
  }
}
